import moment from "moment";

export interface ParkingDurationInfo {
  parkingDurationStart: string;
  parkingDurationEnd: string;
  parkingDurationDuration: string;
}

type ParsedParkingRecord = {
  start: string;
  end: string;
  duration: string;
  sortMoment: moment.Moment | null;
};

export const extractParkingDurationInfo = (
  parkingDurationString: string
): ParkingDurationInfo => {
  const startRe =
    /(Parking start \/ Falschparkbeginn:|Falschparkbeginn:|Ankunft:)\s*(.+?)(?:\r?\n|$)/i;

  const endRe =
    /(Parking end \/ Falschparkende:|Falschparkende:|Abfahrt:|Aktuelle Zeit:)\s*(.+?)(?:\r?\n|$)/i;

  const durationRe =
    /(Parking duration \/ Falschparkdauer:|Falschparkdauer:|Aufenthaltsdauer:)\s*(.+?)(?:\r?\n|$)/i;

  const formats = ["DD.MM.YYYY HH:mm:ss", "DD.MM.YYYY HH:mm"];

  const parseDate = (value: string): moment.Moment | null => {
    if (!value) return null;
    const m = moment(value.trim(), formats, true);
    return m.isValid() ? m : null;
  };

  const blocks = parkingDurationString
    .split(/\n\s*\n+/) // split by empty lines
    .map((block) => block.trim())
    .filter(Boolean);

  const records: ParsedParkingRecord[] = blocks
    .map((block) => {
      const start = block.match(startRe)?.[2]?.trim() ?? "";
      const end = block.match(endRe)?.[2]?.trim() ?? "";
      let duration = block.match(durationRe)?.[2]?.trim() ?? "";

      const sortMoment = parseDate(start) ?? parseDate(end);

      if (!duration && start && end) {
        const startMoment = parseDate(start);
        const endMoment = parseDate(end);

        if (startMoment && endMoment) {
          const diffMin = Math.max(0, endMoment.diff(startMoment, "minutes"));
          duration = `${diffMin} min`;
        }
      }

      return {
        start,
        end,
        duration,
        sortMoment,
      };
    })
    .filter((record) => record.start || record.end || record.duration);

  if (records.length === 0) {
    return {
      parkingDurationStart: "",
      parkingDurationEnd: "",
      parkingDurationDuration: "",
    };
  }

  const latestRecord = records.reduce((latest, current) => {
    if (!latest.sortMoment && current.sortMoment) return current;
    if (!current.sortMoment) return latest;
    if (!latest.sortMoment) return latest;

    return current.sortMoment.isAfter(latest.sortMoment) ? current : latest;
  });

  return {
    parkingDurationStart: latestRecord.start,
    parkingDurationEnd: latestRecord.end,
    parkingDurationDuration: latestRecord.duration,
  };
};